import _ from 'lodash'
import { createGlobalStyle, css, DefaultTheme, Interpolation } from 'styled-components'
import React, { createContext } from 'react'

import { ThemeWrapper } from './themeWrapper'

export interface BriqTheme {
  colors: {
    primary: string
    secondary: string
    info: string
    success: string
    warning: string
    warningBackground: string
    danger: string
    dangerBackground: string

    primaryLightest: string
    primaryLighter: string
    primaryLight: string
    primaryDark: string
    primaryDarker: string
    primaryDarkest: string

    secondaryLightest: string
    secondaryLighter: string
    secondaryLight: string
    secondaryDark: string
    secondaryDarker: string
    secondaryDarkest: string

    orange: string
    pumpkinOrange: string

    lightestGray: string
    lightGray: string
    gray: string
    darkGray: string
    white: string

    bgPrimary: string
    bgSecondary: string
    borderPrimary: string

    textPrimary: string
    textSecondary: string
    textMuted: string

    textPrimaryInverse: string

    buttonTextPrimary: string
    buttonTextSecondary: string
    transparent: string
  }
  fontSizes: Record<'extraSmall' | 'small' | 'medium' | 'large' | 'extraLarge', string>
  titleFontSizes: Record<'extraSmall' | 'small' | 'medium' | 'large' | 'extraLarge', string>
  breakpoints: [string, string, string]
  space: [string, string, string, string, string, string]
}

export type Color = keyof BriqTheme['colors']
export type FontSizes = keyof BriqTheme['fontSizes']
export type TitleFontSizes = keyof BriqTheme['titleFontSizes']
export type Spaces = 0 | 1 | 2 | 3 | 4 | 5
export type Breakpoints = 0 | 1 | 2

export const defaultTheme: BriqTheme = {
  colors: {
    primary: '#003c7d',
    secondary: '#fec74a',
    info: '#17a2b8',
    success: '#28a745',
    warning: '#ff7400',
    warningBackground: '#fff7dd',
    danger: '#dc3545',
    dangerBackground: '#fde6e7',

    primaryLightest: '#CED9E5',
    primaryLighter: '#7E9CBD',
    primaryLight: '#336297',
    primaryDark: '#1F3361',
    primaryDarker: '#192C55',
    primaryDarkest: '#122549',

    secondaryLightest: '#FEF3DA',
    secondaryLighter: '#FEE2A3',
    secondaryLight: '#FED881',
    secondaryDark: '#CB9F3B',
    secondaryDarker: '#98772C',
    secondaryDarkest: '#654F1D',

    orange: '#ff7400',
    pumpkinOrange: '#ff7500',
    lightestGray: '#f4f4f4',
    lightGray: '#f9f9f9',
    gray: '#dfdfdf',
    darkGray: '#505050',
    white: '#FFFFFF',

    bgPrimary: '#f9f9f9',
    bgSecondary: '#E0E0E0',
    borderPrimary: '#CACACA',

    buttonTextPrimary: '#ffffff',
    buttonTextSecondary: '#ffffff',

    textPrimary: 'black',
    textSecondary: '#222',
    textMuted: '#a2a2a2',

    transparent: 'transparent',
    textPrimaryInverse: 'white'
  },
  fontSizes: {
    extraSmall: '12px',
    small: '14px',
    medium: '16px',
    large: '24px',
    extraLarge: '32px'
  },
  titleFontSizes: {
    extraSmall: '14px',
    small: '16px',
    medium: '24px',
    large: '32px',
    extraLarge: '40px'
  },
  // 'breakpoints' must be specified in pixels, see useViewportSize()
  breakpoints: ['1024px', '1024px', '1920px'],
  space: ['0px', '8px', '16px', '24px', '32px', '40px']
}

export const ThemeContext = createContext(defaultTheme)

export const BriqCommonGlobalStyle: React.FC = () => {
  return (
    <ThemeWrapper>
      <GlobalStyle />
    </ThemeWrapper>
  )
}

// html, body, #root => maring is !important because of view html document functionality
const GlobalStyle = createGlobalStyle`
  html, body, #root {
    font-family: 'Montserrat', sans-serif;
    font-size: ${(p: { theme: DefaultTheme }) => p.theme.fontSizes.small};
    font-weight: normal;
    width: 100%;
    height: 100%;
    margin: 0 !important;
  }

  a {
    text-decoration: none;
  }

  .ant-app{
  height:100%;
  }

  .ant-modal-mask {
    background-color: rgba(0, 60, 125, 0.6) !important;
  }

  .briq-dialog {
    z-index: 45000;
    display: flex;
    align-items: center;
    justify-content: center;

    .ant-modal {
      position: static;
      margin: 0;
      padding: 0;
      max-height: 100vh;
    }

    .ant-modal-close-x {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: large;
      svg {
        fill: white;
      }
    }

    .ant-modal-content {
      border-radius: 4px;
      position: relative;
      overflow: hidden;
      padding: 0;
    }

    .ant-modal-header {
      padding: 20px 11%;
      box-sizing: border-box;
      border-bottom: none;
    }

    .ant-modal-body {
      padding: 0;
      max-width: 100vw;
    }

    .ant-modal-footer {
      padding: 15px 10% 15px 10%;
      border-top: none;
    }

    ${brkmax(
      1,
      css`
        & {
          right: auto;
          bottom: auto;
          width: 100vw;
          height: 100vh;
        }

        .ant-modal {
          position: fixed !important;
          top: 0;
          padding: 0;
          margin: 0;
          width: unset !important;
          left: 0;
          overflow: auto;
          min-width: 100%;
          max-width: 100%;
          height: 100%;
        }

        .ant-modal-content {
          border-radius: 0;
          max-height: 100vh;
        }

        .ant-modal-header {
          padding: 10px 5%;
        }

        .ant-modal-close {
          top: 0;
          width: 45px;
        }

        .ant-modal-body {
          max-height: calc(100vh - 56px);
          overflow-y: auto;
        }

        .dialog-body-wrapper {
          margin: 0;
          padding: 5%;
        }

        .ant-modal-close-x {
          width: 60px;
          height: 60px;
          svg {
            width: 1.4em !important;
            height: 1.4em !important;
            fill: ${defaultTheme.colors.primary};
          }
        }
      `
    )}
  }

  .ant-select-dropdown {
    z-index: 46000;
    border-radius: 2px;
    padding: 5px 0px;
  }

  .ant-select-dropdown .ant-select-item{
    border-radius: 0px; 
  }

  .ant-select-selection-item {
    .investigation-product-select-option svg:nth-of-type(1), .import-check-products-product-select-option svg:nth-of-type(1) {
      display: none;
    }
  }

  .ant-select-item-option-content {
    .investigation-product-select-option svg:nth-of-type(2), .import-check-products-product-select-option svg:nth-of-type(2) {
      display: none;
    }
  }

  .select-single-item-text-field .investigation-product-select-option svg {
    display: none;
  }

  .ant-picker-dropdown {
    z-index: 46000;
  }

  .ant-dropdown-menu {    
    box-shadow: 0 8px 16px -4px ${themeColor('primary')};
    border-radius: 4px !important;
    padding-top: 5px;
    padding-bottom: 5px;
    background-color:${themeColor('primary')};   
  }
 
 .ant-select.primaryColorVariantSelect .ant-select-selection-item {
  color: ${themeColor('white')} !important;
  font-weight:normal !important;
 }

 .ant-select-single.ant-select-open .ant-select-selection-item {
  color: #bfbfbf !important;
}
  
.ant-select.disabledSelectionItem {
    background: ${themeColor('primary')};
    color:${themeColor('white')};
 }

 .ant-select.pagination .ant-select-selection-placeholder{
	color:${themeColor('white')} !important;
 }

 .ant-select.pagination .ant-select-selection-item{
	color:${themeColor('white')} !important;
 }

 .ant-select.pagination .ant-select-arrow{
	color:${themeColor('white')} !important;
 }

 .ant-select.pagination .ant-select-selector{
	background-color:${themeColor('primary')} !important;
 }
.ant-checkbox-inner {
    border-radius: 4px;
    border: 1px solid ${themeColor('borderPrimary')};
  }

  .ant-checkbox-checked {
    .ant-checkbox-inner {
      background-color: ${themeColor('primary')};
      border-color: ${themeColor('primary')};
    }
  }

  .ant-tooltip {
    max-width: 40vw;

    .ant-tooltip-inner {
      padding: 16px;
      border-radius: 4px;
    }
    .ant-tooltip-arrow {
      width: 18px;
      height: 18px;

      .ant-tooltip-arrow-content {
        width: 15px;
        height: 15px;
        border-radius: 4px;
      }
    }
  }

  .opacity {
    opacity: 0.01;

    &.opacity-enter-active {
      opacity: 1;
      transition: opacity 500ms ease-in;
    }
  }

  .opacity-leave {
    opacity: 1;
    &.opacity-leave-active {
      opacity: 0.01;
      transition: opacity 300ms ease-in;
    }
}

.mutlicolumn-dropdown-select
{
	position: fixed;
	background-color: ${themeColor('white')};
	margin-left: -88px;
	margin-top: -4px;
	border-radius: 4px;
    border: 1px solid black;
	font-size: ${themeSpace(2)};
    scrollbar-width: none;
    color:${themeColor('textPrimary')};
   ul {
        list-style: none;
        padding-right:${themeSpace(2)};
        padding-left:${themeSpace(3)};
        padding-top:${themeSpace(1)};
        font-family: 'Montserrat', sans-serif;
      }

    li{
        padding-top:${themeSpace(1)};
        padding-bottom:${themeSpace(1)};
        span{
            cursor: pointer;
        }
        &.selectedOption{
            background-color: ${themeColor('primaryLightest')};
            border-radius: 4px;
        }
      }
    li:hover{
        background-color: ${themeColor('lightestGray')};
        border-radius: 4px;
    }
    .ant-input
    {
        position:fixed;
        padding-bottom:${themeSpace(1)};
        background-color: ${themeColor('lightestGray')};
    }
    .itemsContainer {
        max-height:400px;
    }
}

.ant-select-dropdown:has(.mutlicolumn-dropdown-select-mobile) {
    padding: 0;
    top: 60px !important;
}

.mutlicolumn-dropdown-select-mobile
{
	background-color: ${themeColor('white')};
	border-radius: 4px;
    border: 1px solid black;
    font-size: ${themeSpace(2)};;
    color:${themeColor('textPrimary')};
    ul {
        list-style: none;
        padding-left:0px;
        font-family: 'Montserrat', sans-serif;
        width: 100%;
      }

    li{
        padding-top:5px;
        padding-bottom:5px;
        span{
            cursor: pointer;
        }
        &.selectedOption{
            background-color: ${themeColor('primaryLightest')};
            border-radius: 4px;
        }
      }
    li:hover{
        background-color: ${themeColor('lightestGray')};
        border-radius: 4px;
    }
    .ant-input
    {
        padding-bottom:${themeSpace(1)};
        background-color: ${themeColor('lightestGray')};
    }
    .itemsContainer {
        max-height:300px;
    }
}
.mutlicolumn-dropdown-space
{
    padding: 0 8px 4px;
    display: list-item;
}
.mutlicolumn-dropdown-select,.mutlicolumn-dropdown-select-mobile {
    .itemsContainer {
    display: flex;
    overflow: auto;
    }
}

`

export function themeColor(color: Color): (p: { theme: DefaultTheme }) => string {
  return ({ theme }) => theme.colors[color]
}

export function themeSpace(space: Spaces): (p: { theme: DefaultTheme }) => string {
  return ({ theme }) => theme.space[space]
}

export function themeFontSize(size: FontSizes): (p: { theme: DefaultTheme }) => string {
  return ({ theme }) => theme.fontSizes[size]
}

export function themeTitleFontSize(size: TitleFontSizes): (p: { theme: DefaultTheme }) => string {
  return ({ theme }) => theme.titleFontSizes[size]
}

export function brkmin(min: Breakpoints | string, styles: Interpolation<object>) {
  if (_.isString(min)) {
    return css`
      @media (min-width: ${min}) {
        ${styles}
      }
    `
  }

  return css`
    @media (min-width: ${defaultTheme.breakpoints[min]}) {
      ${styles}
    }
  `
}

export function brkmax(max: Breakpoints | string, styles: Interpolation<object>) {
  if (_.isString(max)) {
    return css`
      @media (max-width: ${max}) {
        ${styles}
      }
    `
  }

  return css`
    @media (max-width: ${defaultTheme.breakpoints[max]}) {
      ${styles}
    }
  `
}

export function brkbt(min: Breakpoints, max: Breakpoints, styles: Interpolation<object>) {
  return `@media (min-width: ${defaultTheme.breakpoints[min]}) and (max-width: ${defaultTheme.breakpoints[max]}) { ${styles} }
  `
}

export const breakpoint = {
  mobile: (styles: Interpolation<object>) => styles,
  tablet: (styles: Interpolation<object>) => brkmin(0, styles),
  desktop: (styles: Interpolation<object>) => brkmin(1, styles),
  wide: (styles: Interpolation<object>) => brkmin(2, styles)
}

export function hexToRgb(hex: string, asString = true) {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  hex = _.replace(hex, shorthandRegex, (m, r, g, b) => r + r + g + g + b + b)

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  const rgb = result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : ''

  if (asString && rgb) {
    return `${rgb.r},${rgb.g},${rgb.b}`
  }
  return rgb
}
